import React, { useMemo, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Layers, Search, Eye, MousePointer, Zap, Image, Calculator, PenTool, Clock, Coins, BarChart3 } from 'lucide-react';
import { StatCard } from '../../../shared/StatCard';
import { Card } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { type Question, QUESTION_TYPE_REGISTRY } from '../../../../content';
interface TypesTabProps {
  questions: Question[];
  onSelectType: (type: string) => void;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 350,
      damping: 26
    }
  }
};
const getTypeIcon = (type: string) => {
  if (['multiple_choice', 'cijferend_optellen', 'cijferend_aftrekken', 'number_sequence', 'ratio_table'].includes(type)) return Calculator;
  if (['fill_in', 'drag_drop_fill', 'matching', 'ordering', 'text_highlight'].includes(type)) return PenTool;
  if (['clock', 'schedule_reading'].includes(type)) return Clock;
  if (['money'].includes(type)) return Coins;
  if (['bar_chart', 'line_chart', 'pie_chart', 'table_reading'].includes(type)) return BarChart3;
  return Layers;
};
const TYPE_COLORS = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500', 'bg-teal-500', 'bg-cyan-500'];
export function TypesTab({
  questions,
  onSelectType
}: TypesTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const totalQuestions = questions.length;
  // Count questions per type
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    questions.forEach(q => {
      counts[q.questionType] = (counts[q.questionType] || 0) + 1;
    });
    return counts;
  }, [questions]);
  // Build type list with metadata
  const typesList = useMemo(() => {
    return Object.entries(QUESTION_TYPE_REGISTRY).map(([type, config]) => ({
      type,
      ...config,
      count: typeCounts[type] || 0,
      usagePct: totalQuestions > 0 ? Math.round((typeCounts[type] || 0) / totalQuestions * 100) : 0,
      exampleQuestion: questions.find(q => q.questionType === type)?.question || 'Geen voorbeeld beschikbaar'
    })).sort((a, b) => b.count - a.count);
  }, [typeCounts, totalQuestions, questions]);
  // Filter by search
  const filteredTypes = useMemo(() => {
    if (!searchQuery.trim()) return typesList;
    const query = searchQuery.toLowerCase();
    return typesList.filter(t => t.label.toLowerCase().includes(query) || t.type.toLowerCase().includes(query));
  }, [typesList, searchQuery]);
  // Stats
  const activeTypes = typesList.filter(t => t.count > 0).length;
  const totalTypes = typesList.length;
  return <div className="space-y-6" data-id="element-3842">
      {/* Stats Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-3843">
        <motion.div variants={itemVariants} data-id="element-3844">
          <StatCard label="Geregistreerde Typen" value={totalTypes} icon={<Layers className="w-5 h-5" data-id="element-3846" />} color="blue" data-id="element-3845" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3847">
          <StatCard label="Actief in Gebruik" value={activeTypes} icon={<Zap className="w-5 h-5" data-id="element-3849" />} color="emerald" data-id="element-3848" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3850">
          <StatCard label="Totaal Vragen" value={totalQuestions.toLocaleString('nl-NL')} icon={<Calculator className="w-5 h-5" data-id="element-3852" />} color="purple" data-id="element-3851" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3853">
          <StatCard label="Interactieve Typen" value={typesList.filter(t => t.isInteractive && t.count > 0).length} icon={<MousePointer className="w-5 h-5" data-id="element-3855" />} color="amber" data-id="element-3854" />
        </motion.div>
      </motion.div>

      {/* Distribution Bar + Search */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3856">
        <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3857">
          <div data-id="element-3858">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-3859">
              <Layers className="w-5 h-5 text-blue-500" data-id="element-3860" />
              Vraagtypen Overzicht
            </h3>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-3861">
              {activeTypes} van {totalTypes} typen actief in gebruik
            </p>
          </div>
          <div className="w-full sm:w-64" data-id="element-3862">
            <Input type="text" placeholder="Zoek vraagtype..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-3864" />} variant="admin" size="sm" data-id="element-3863" />
          </div>
        </div>

        {/* Distribution visualization */}
        <div className="px-4 sm:px-5 pt-4 pb-2" data-id="element-3865">
          <div className="flex items-center justify-between mb-2" data-id="element-3866">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3867">
              Verdeling ({totalQuestions.toLocaleString('nl-NL')} vragen)
            </span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-3868">
            {typesList.map((t, i) => {
            if (t.count === 0) return null;
            return <motion.div key={t.type} initial={{
              width: 0
            }} animate={{
              width: `${t.usagePct}%`
            }} transition={{
              duration: 0.6,
              delay: i * 0.02
            }} className={`h-full ${TYPE_COLORS[i % TYPE_COLORS.length]}`} title={`${t.label}: ${t.count} (${t.usagePct}%)`} data-id="element-3869" />;
          })}
          </div>
        </div>

        {/* Type Cards Grid */}
        <div className="p-4 sm:p-5" data-id="element-3870">
          {filteredTypes.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-3871">
              Geen vraagtypen gevonden voor "{searchQuery}"
            </div> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3872">
              {filteredTypes.map((t, index) => {
            const Icon = getTypeIcon(t.type);
            return <motion.div key={t.type} variants={itemVariants} data-id="element-3873">
                    <Card className="p-5 flex flex-col h-full border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group" data-id="element-3874">
                      <div className="flex justify-between items-start mb-4" data-id="element-3875">
                        <div className="flex items-center gap-3" data-id="element-3876">
                          <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 group-hover:scale-105 transition-transform" data-id="element-3877">
                            <Icon className="w-5 h-5" data-id="element-3878" />
                          </div>
                          <div className="min-w-0" data-id="element-3879">
                            <h3 className="font-bold text-gray-900 text-sm truncate" data-id="element-3880">
                              {t.label}
                            </h3>
                            <code className="text-[10px] text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-3881">
                              {t.type}
                            </code>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0" data-id="element-3882">
                          <div className={`text-xl font-black ${t.count > 0 ? 'text-blue-600' : 'text-gray-300'}`} data-id="element-3883">
                            {t.count}
                          </div>
                          <div className="text-[10px] text-gray-400 font-medium" data-id="element-3884">
                            {t.usagePct}%
                          </div>
                        </div>
                      </div>

                      {/* Example question */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-4 flex-1 border border-gray-100" data-id="element-3885">
                        <p className="text-[10px] text-gray-500 font-semibold mb-1 uppercase tracking-wider" data-id="element-3886">
                          Voorbeeld
                        </p>
                        <p className="text-xs text-gray-700 line-clamp-2 italic" data-id="element-3887">
                          "{t.exampleQuestion}"
                        </p>
                      </div>

                      {/* Badges + Action */}
                      <div className="mt-auto space-y-3" data-id="element-3888">
                        <div className="flex flex-wrap gap-1.5" data-id="element-3889">
                          {t.isInteractive ? <Badge variant="info" size="sm" data-id="element-3890">
                              <MousePointer className="w-3 h-3 mr-1" data-id="element-3891" />
                              Interactief
                            </Badge> : <Badge variant="neutral" size="sm" data-id="element-3892">
                              Statisch
                            </Badge>}
                          {t.isCitoEligible && <Badge variant="success" size="sm" data-id="element-3893">
                              <Zap className="w-3 h-3 mr-1" data-id="element-3894" />
                              CITO
                            </Badge>}
                          {t.hasBuiltInVisuals && <Badge variant="warning" size="sm" data-id="element-3895">
                              <Image className="w-3 h-3 mr-1" data-id="element-3896" />
                              Visuals
                            </Badge>}
                        </div>
                        <Button variant="secondary" size="sm" className="w-full justify-center" onClick={() => onSelectType(t.type)} disabled={t.count === 0} data-id="element-3897">
                          <Eye className="w-4 h-4 mr-1.5" data-id="element-3898" />
                          Bekijk {t.count} vragen
                        </Button>
                      </div>
                    </Card>
                  </motion.div>;
          })}
            </motion.div>}
        </div>
      </div>
    </div>;
}